import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng';
import { SelectItem } from 'primeng';
import { Message } from '../../classes/Message';
import { IIncidentTypeDTO, IIncidentTypeServiceGroupAssigneeDTO } from '../../interfaces/incident';
import { IncidentsService } from '../../services';
import { OrganisationsService } from '../../services';
import { IDropdownItem } from '../../interfaces/drop-down-item';
import { compare, deepClone } from 'fast-json-patch';

@Component({
  selector: 'incident-admin',
  templateUrl: 'incident-admin.component.html',
  styleUrls: ['incident-admin.component.scss'],
})
export class IncidentAdminComponent implements OnInit {
  constructor(
    private incidentsService: IncidentsService,
    private messageService: MessageService,
    private organisationService: OrganisationsService,
  ) {}

  incidentTypes: IIncidentTypeDTO[];
  incidentTypeClones: IIncidentTypeDTO[];

  assignees: IDropdownItem[]; // has extra user info in the 'value' property, i.e. serviceDeliveryGroupId etc.
  assigneesOptions: SelectItem[] = [];

  serviceGroups: SelectItem[] = [];
  assignableServiceGroups: SelectItem[] = []; // service groups for the selected incident type with no assignee

  // allowable assignees per service group (users who have been assigned to that particular group)
  serviceGroupAssignees = new Map<number, SelectItem[]>();

  // map (incident type = key, service group assignees array = value)
  editingAssignee: IIncidentTypeServiceGroupAssigneeDTO;

  assigningIncidentType = null;

  selectedIncidentType: IIncidentTypeDTO = null;
  showAssigneeDialog = false;
  newGroupAssignment = { serviceDeliveryGroupId: null, assigneeId: null, isNew: false };

  async ngOnInit() {
    // load closure types
    this.loadTypes();
    await this.loadAssignees();
    this.loadServiceGroups();
  }

  loadTypes() {
    this.incidentsService.getIncidentTypes().then((res) => {
      //console.log('Incident admin types: ', res);
      this.incidentTypes = res;

      // clone for patching
      this.incidentTypeClones = deepClone(this.incidentTypes);
    });
  }

  async loadAssignees() {
    await this.incidentsService.getAssignees().then((result) => {
      //console.log("Assignees: ", result);
      this.assignees = result;
      this.assigneesOptions = result
        .sort((a, b) => a.label.trim().localeCompare(b.label.trim()))
        .map((x) => <SelectItem>{ value: x.value.id, label: x.label });

      // add a default for null so it is selectable in the dropdown
      //this.assigneesOptions.unshift({ label: 'Not Assigned', value: null });
    });
  }

  loadServiceGroups() {
    this.serviceGroups = [];

    this.serviceGroups = [{ label: 'None', value: null }];
    this.organisationService.getServiceDeliveryGroups().then((result) => {
      result.forEach((item) => {
        this.serviceGroups.push({ label: item.name, value: item.id });
      });

      // map assignees to service groups
      this.serviceGroups.forEach((sg) => {
        // get all users in this service group
        let res = this.assignees.filter(
          (x) => x.value.serviceDeliveryGroupId == sg.value || !sg.value,
        );

        if (res && res.length > 0) {
          this.serviceGroupAssignees.set(
            sg.value,
            res.map((r) => <SelectItem>{ value: r.value.id, label: r.label }),
          );
        }
      });

      //console.log('Service group assignees: ', this.serviceGroupAssignees);
    });
  }

  reactivateIncidentType(item: IIncidentTypeDTO) {
    this.incidentsService.activateIncidentType(item.id).then(() => this.loadTypes());
  }

  deactivateIncidentType(item: IIncidentTypeDTO) {
    this.incidentsService.deactivateIncidentType(item.id).then(() => this.loadTypes());
  }

  assignOpenIncidents(event: Event, item) {
    event.preventDefault();
    this.assigningIncidentType = item;

    this.incidentsService
      .assigneeOpenToUser(item.id, item.reassignUser)
      .then(() => {
        this.assigningIncidentType = null;
        this.messageService.add(
          Message.Info(`Open incidents assigned to ${item.reassignUserName}`),
        );
      })
      .catch((res) => {
        this.assigningIncidentType = null;
        this.messageService.add(Message.Error('Details not saved', 'Error'));
      });
  }

  setReassignUser(item, event) {
    let user = this.assigneesOptions.find((x) => x.value == event.value);

    item.reassignUser = event.value;
    item.reassignUserName = user.label;
  }

  addAssignee(item: IIncidentTypeDTO) {
    let sga = this.incidentTypes.map((x) => x.incidentTypeServiceGroupAssignees);
    const flattened = [].concat(...sga).filter((x) => x);
    let usedGroups = flattened
      .filter((x) => x.incidentTypeId == item.id)
      .map((x) => x.serviceDeliveryGroupId);

    //console.log("SGA: ", sga, "  -  FLAT: ", flattened, "  - Used: ");

    this.assignableServiceGroups = this.serviceGroups.filter((x) => !usedGroups.includes(x.value));

    // if all service groups (and the 'all' group) have an assignee, cannot add any more
    if (!this.assignableServiceGroups || this.assignableServiceGroups.length == 0) {
      this.messageService.add(
        Message.Error(`Incident type already has auto-assignees for all possible service groups`),
      );
      return;
    }

    this.newGroupAssignment.serviceDeliveryGroupId = null;
    this.newGroupAssignment.assigneeId = null;
    this.newGroupAssignment.isNew = true;

    this.selectedIncidentType = item;
    this.showAssigneeDialog = true;
  }

  deleteAssignee(incidentType: IIncidentTypeDTO, assignee: IIncidentTypeServiceGroupAssigneeDTO) {
    this.incidentsService
      .deleteIncidentTypeAssignee(assignee.id)
      .then(() => {
        incidentType.incidentTypeServiceGroupAssignees =
          incidentType.incidentTypeServiceGroupAssignees.filter((x) => x.id != assignee.id);
      })
      .catch((res) =>
        this.messageService.add(Message.Error('Could not delete assignment', 'Error')),
      );
  }

  editAssignee(item: IIncidentTypeDTO, assignee: IIncidentTypeServiceGroupAssigneeDTO) {
    this.newGroupAssignment.serviceDeliveryGroupId = assignee.serviceDeliveryGroupId;
    this.newGroupAssignment.assigneeId = assignee.autoAssigneeUserId;
    this.newGroupAssignment.isNew = false;

    this.selectedIncidentType = item;
    this.editingAssignee = assignee;
    this.showAssigneeDialog = true;
  }

  setAssignee() {
    const dto = <IIncidentTypeServiceGroupAssigneeDTO>{
      autoAssigneeUserId: this.newGroupAssignment.assigneeId,
      serviceDeliveryGroupId: this.newGroupAssignment.serviceDeliveryGroupId,
      incidentTypeId: this.selectedIncidentType.id,
    };

    this.incidentsService
      .setIncidentTypeAssignee(dto)
      .then((result) => {
        const user = this.assignees.find((x) => x.value.id == this.newGroupAssignment.assigneeId);

        if (user) {
          this.messageService.add(
            Message.Info(
              `Incident type will be auto-assigned to ${user.value.firstName} ${user.value.lastName}`,
            ),
          );
        }

        let incidentType = this.incidentTypes.find((x) => x.id == this.selectedIncidentType.id);

        if (incidentType) {
          if (this.newGroupAssignment.isNew) {
            if (!incidentType.incidentTypeServiceGroupAssignees) {
              incidentType.incidentTypeServiceGroupAssignees = [];
            }
            incidentType.incidentTypeServiceGroupAssignees.push(result);
          } else {
            let assignee = incidentType.incidentTypeServiceGroupAssignees.find(
              (x) =>
                x.serviceDeliveryGroupId == this.newGroupAssignment.serviceDeliveryGroupId ||
                (!x.serviceDeliveryGroupId && !this.newGroupAssignment.serviceDeliveryGroupId),
            );

            if (assignee) {
              assignee.autoAssigneeUserId = result.autoAssigneeUserId;
              assignee.autoAssigneeUserName = result.autoAssigneeUserName;
            }
          }
        }

        this.showAssigneeDialog = false;
      })
      .catch((err) => {
        this.messageService.add(Message.Error('Could not update assignment', 'Error'));
      });
  }

  setAutoClose(item: any) {
    const originalItem = this.incidentTypeClones.find((x) => x.id == item.id);

    const patch = compare(originalItem, item);
    this.incidentsService.patchIncidentType(item.id, patch).then(() => {
      // re-clone incident type items
      this.incidentTypeClones = deepClone(this.incidentTypes);
    });
  }
}
